import { Box, Typography, useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import InfoBox from "../../boxContainer/infoBox.component";
import { useNavigate } from "react-router";
import GisFormatDialog from "../../muiDialogs/gisFormatDialog.component";
import { useEffect, useState } from "react";
import { calculatePolygonArea, isCurrentCulture } from "../utils";
import UnderlineButton from "../../buttons/underlineButton.component";
import SensorDialog from "../../muiDialogs/sensorDialog.component";
import CustomAlert from "../../alert/alert.component";
import { resetUpdateSensorState } from "../../../store/features/customer/sensor/sensorSlice";
import SensorBoxSkeleton from "../../skeletons/sensorBoxSkeleton.component";
import {
  fetchUnassignedSensorsStart,
} from "../../../store/features/customer/instruments/sensorsSlice";
import SensorBox from "../../boxContainer/sensorBox.component";
import { isPointInPolygon } from "../../map/mapUtils";
import { getErrorMessage } from "../../../utils/errorUtils";

const FieldTabPanel = ({ field, sensors, theme }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDesktopXl = useMediaQuery(theme.breakpoints.down("xl"));

  const [openDialog, setOpenDialog] = useState(false);
  const [openSensorDialog, setOpenSensorDialog] = useState(false);
  const [isSensorFirstTimeAdded, setIsSensorFirstTimeAdded] = useState(false);
  const [positionMarkerWarning, setPositionMarkerWarning] = useState(false);

  const { isLoading } = useSelector((state) => state.sensors);
  const { success, error, operationType } = useSelector(
    (state) => state.sensor
  );
  const accessToken = useSelector((state) => state.auth?.user?.access_token);
  const { isActive: isDragModeActive } = useSelector(
    (state) => state.dragMode
  );
  const { newPosition } = useSelector((state) => state.marker);

  const cultures = field?.field_colture || [];
  const lastCulture = cultures[cultures.length - 1];

  const currentCulture = isCurrentCulture(lastCulture) ? lastCulture : null;
  const areaMq = calculatePolygonArea(field?.polygon);

  const handleSensorAdded = (isFirstTime) => {
    setIsSensorFirstTimeAdded(isFirstTime);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleAlertClose = () => {
    dispatch(resetUpdateSensorState());
    setOpenDialog(false);
    setIsSensorFirstTimeAdded(false);
  };

  useEffect(() => {
    if (success) {
      setOpenSensorDialog(false);
    }
  }, [success]);

  useEffect(() => {
    if (openSensorDialog && accessToken) {
      dispatch(
        fetchUnassignedSensorsStart({
          accessToken,
          field: "",
          has_field: false,
        })
      );
    }
  }, [accessToken, dispatch, openSensorDialog]);

  useEffect(() => {
    if (
      !isDragModeActive ||
      !Array.isArray(newPosition) ||
      newPosition.length < 2 ||
      !field?.polygon?.length
    ) {
      setPositionMarkerWarning(false);
      return;
    }

    const isInsidePolygon = isPointInPolygon(newPosition, field.polygon);

    setPositionMarkerWarning(!isInsidePolygon);
  }, [field?.polygon, isDragModeActive, newPosition]);

  return (
    <Box
      width="100%"
      height="100%"
      py={2}
      display="flex"
      flexDirection="column"
      gap={2}
    >
      <InfoBox
        theme={theme}
        label={field?.name}
        buttonLabel="Modifica terreno"
        onClick={() => navigate(`/userLands/updateField/${field?.id}`)}
      />
      <InfoBox
        theme={theme}
        label="File kml"
        buttonLabel="Esporta file kml"
        onClick={() => setOpenDialog(true)}
      />
      <InfoBox
        theme={theme}
        label="Superficie"
        actions={false}
        data={`${areaMq?.toFixed(6)} km²`}
      />
      <InfoBox
        theme={theme}
        label="Coltura"
        actions={false}
        data={currentCulture?.colture?.name || ""}
      />
      <Box
        width="100%"
        sx={{
          backgroundColor: theme?.palette?.custom?.backgroundColor,
          borderRadius: "2px",
        }}
        p={1}
      >
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontWeight="bold" fontSize="14px">
            {`Sensori (${sensors?.length})`}
          </Typography>
          <UnderlineButton
            label="Aggiungi sensore"
            onClick={() => setOpenSensorDialog(true)}
          />
        </Box>
        <Box
          display="grid"
          alignItems="center"
          gap={1}
          mt={1}
          width="100%"
          maxHeight="305px"
          gridTemplateColumns={isDesktopXl ? "1fr" : "1fr 1fr"}
          overflow="auto"
        >
          {isLoading
            ? Array.from({ length: 2 }).map((_, index) => (
                <SensorBoxSkeleton key={index} />
              ))
            : sensors?.map((tool) => (
                <SensorBox key={tool.id} instrumentsData={tool} field={field} />
              ))}
        </Box>
      </Box>
      <GisFormatDialog
        onClose={handleCloseDialog}
        open={openDialog}
        field={field}
      />
      <SensorDialog
        open={openSensorDialog}
        onClose={() => setOpenSensorDialog(false)}
        field={field}
        accessToken={accessToken}
        onSensorAdded={handleSensorAdded}
      />
      {success && isSensorFirstTimeAdded && (
        <CustomAlert
          severity="success"
          onClose={handleAlertClose}
          message="Sensore aggiunto al terreno"
        />
      )}
      {error && (
        <CustomAlert
          severity="error"
          onClose={handleAlertClose}
          message={getErrorMessage(error, {
            fallback: "Errore durante l'aggiunta del sensore",
          })}
        />
      )}
      {success && operationType === "position" && !isSensorFirstTimeAdded && (
        <CustomAlert
          severity="success"
          onClose={handleAlertClose}
          message="Posizione sensore aggiornata con successo"
        />
      )}
      {success && operationType === "update" && !isSensorFirstTimeAdded && (
        <CustomAlert
          severity="success"
          onClose={handleAlertClose}
          message="Posizione sensore aggiornata con successo"
        />
      )}
      {positionMarkerWarning && (
        <CustomAlert
          severity="warning"
          title="Attenzione"
          onClose={() => setPositionMarkerWarning(false)}
          message="Sensore fuori dal poligono del terreno"
          autoHideDuration={false}
          theme={theme}
          bottom={null}
          top={150}
          left="40%"
        />
      )}
    </Box>
  );
};

export default FieldTabPanel;
